import { Injectable } from '@angular/core';
import { Filesystem } from '@capacitor/filesystem';

import type { FtpConnectionConfig, FtpFileEntry, FtpTransport } from '../../shared/services/ftp-transport/ftp-transport.port';

import { MOCK_HUNTING_DEVICE_MEDIA } from './mock-hunting-device-media.fixture';
import type { MockHuntingDeviceFile } from './mock-hunting-device-media.fixture';

// Matches the real `CANNES_FTP_BASE_DIR` in `hunting-product-sync.effects.ts` — the transport
// interface treats remote paths as opaque strings, so this is a naming convention, not a shared
// import (the effects' constant is private, by design — nothing outside it should depend on it).
const MOCK_ROOT_DIR = 'userdata';

/**
 * Implements `FtpTransport` as-is (see `ftp-transport.port.ts`) so it drops straight into the
 * `FTP_TRANSPORT` DI token and feeds the real `HuntingProductSyncEffects` engine unchanged.
 * `list('userdata')` returns month directories; `list('userdata/<yyyy-mm>')` returns files named
 * `img_…`/`vid_…` — the prefix + date pattern the engine filters on and parses `capturedAt` from.
 * `downloadToFile` copies bytes from a bundled asset into the requested local URI; those bytes
 * must pass the engine's JPEG/MP4 integrity verify (see `mock-hunting-device-media.fixture.ts`).
 */
@Injectable()
export class MockHuntingMediaTransport implements FtpTransport {
  // Assets never change at runtime — fetch each one once and reuse the cached blob for both
  // size() (called twice, 2s apart, and must return the SAME value both times) and downloadToFile().
  private readonly blobCache = new Map<string, Promise<Blob>>();

  connect(_config: FtpConnectionConfig): Promise<void> {
    return Promise.resolve();
  }

  disconnect(): Promise<void> {
    return Promise.resolve();
  }

  async list(remoteDirectory: string): Promise<Array<FtpFileEntry>> {
    if (remoteDirectory === MOCK_ROOT_DIR) {
      const monthDirectories = Array.from(new Set(MOCK_HUNTING_DEVICE_MEDIA.map(item => item.monthDir)));
      return monthDirectories.map(name => ({ name, size: 0, isDirectory: true }));
    }

    const monthDirectory = remoteDirectory.slice(MOCK_ROOT_DIR.length + 1);
    const filesInMonth = MOCK_HUNTING_DEVICE_MEDIA.filter(item => item.monthDir === monthDirectory);
    return Promise.all(
      filesInMonth.map(async (item): Promise<FtpFileEntry> => {
        const blob = await this.fetchAssetBlob(item.assetPath);
        return { name: item.deviceFileName, size: blob.size, isDirectory: false };
      })
    );
  }

  async size(remotePath: string): Promise<number> {
    const entry = this.resolveEntry(remotePath);
    const blob = await this.fetchAssetBlob(entry.assetPath);
    return blob.size;
  }

  async downloadToFile(remotePath: string, localPath: string): Promise<void> {
    const entry = this.resolveEntry(remotePath);
    const blob = await this.fetchAssetBlob(entry.assetPath);
    const base64Data = await this.blobToBase64(blob);
    // No `directory` option — Filesystem then treats `path` as an already-resolved absolute URI,
    // which is exactly what `localPath` is here (the real transport writes to the same kind of URI).
    await Filesystem.writeFile({ path: localPath, data: base64Data });
  }

  private resolveEntry(remotePath: string): MockHuntingDeviceFile {
    const fileName = remotePath.slice(remotePath.lastIndexOf('/') + 1);
    const entry = MOCK_HUNTING_DEVICE_MEDIA.find(item => item.deviceFileName === fileName);
    if (!entry) {
      throw new Error(`MockHuntingMediaTransport: no bundled asset for remote path "${remotePath}"`);
    }
    return entry;
  }

  private fetchAssetBlob(assetPath: string): Promise<Blob> {
    let cached = this.blobCache.get(assetPath);
    if (!cached) {
      cached = fetch(assetPath).then(response => {
        if (!response.ok) {
          throw new Error(`MockHuntingMediaTransport: failed to fetch bundled asset "${assetPath}" (${response.status})`);
        }
        return response.blob();
      });
      this.blobCache.set(assetPath, cached);
    }
    return cached;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener('loadend', () => {
        const result = reader.result as string;
        const commaIndex = result.indexOf(',');
        resolve(commaIndex === -1 ? result : result.slice(commaIndex + 1));
      });
      reader.addEventListener('error', () => reject(reader.error ?? new Error('MockHuntingMediaTransport: failed to read bundled asset')));
      reader.readAsDataURL(blob);
    });
  }
}
